import {Trans} from '@lingui/macro';
import React, {FC, useCallback, useEffect} from 'react';

import {AsyncStatusesEnum} from '../../../../libs/shared/helpers/src/types/asyncStatuses';
import {useLinkNavigation} from '../../../components/common/navigation/useLinkNavigation';
import {LocalApplicationModel} from '../../../models/api/localApplicationModel';
import {UnsavedOauthClientModel} from '../../../models/api/oauthClientModel';
import {buildSettingsPath} from '../../../models/url/settingsUrlHelpers';
import {
  SettingsApplicationComponentsEnum,
  SettingsOauthClientUrl,
  SettingsResourcePagesEnum,
  SettingsRootPagesEnum,
} from '../../../models/url/settingsUrlModel';
import {SettingsPanelId} from '../../../store/panels/panelStateBase';
import {useFetchOauthClient} from '../../fetchers/oauthClient';
import {saveOauthClientAsync} from '../../fetchers/oauthClientMutations';
import {OauthClientDetails} from './oauthClientDetails';
import {OauthClientEditorSkeleton} from './oauthClientEditorSkeleton';
import {OauthClientEditorWrapper} from './oauthClientEditorWrapper';

/*
 * Props.
 */

interface SettingsOauthClientProps {
  panelId: SettingsPanelId;
  url: SettingsOauthClientUrl;
  application: LocalApplicationModel;
  setPageTitle: (title: React.ReactNode) => void;
  setPageActions: (title: React.ReactNode) => void;
  onRefetchApplication: () => void;
}

/*
 * Component.
 */

export const SettingsOauthClient: FC<SettingsOauthClientProps> = (props) => {
  const {panelId, url, application, setPageTitle, setPageActions, onRefetchApplication} = props;
  const {resourcePage, applicationId} = url;

  const {oauthClient, status: oauthClientStatus, refetch} = useFetchOauthClient(application);

  useEffect(() => {
    refetch();
  }, [application, refetch]);

  const {navigateToLink} = useLinkNavigation();
  const navigateTo = useCallback(
    (page: SettingsResourcePagesEnum, id?: number) =>
      navigateToLink(
        buildSettingsPath({
          rootPage: SettingsRootPagesEnum.APPLICATIONS,
          componentPage: SettingsApplicationComponentsEnum.OAUTH_CLIENT,
          resourcePage: page,
          applicationId,
          searchQuery: undefined,
        }),
      ),
    [applicationId, navigateToLink],
  );

  useEffect(() => {
    setPageTitle(computePageTitle(url, application));
    setPageActions(null);
  }, [application, resourcePage, setPageActions, setPageTitle, url]);

  if (oauthClientStatus !== AsyncStatusesEnum.LOADED) {
    return <OauthClientEditorSkeleton oauthClient={oauthClient} />;
  }

  if (resourcePage === SettingsResourcePagesEnum.OVERVIEW) {
    return <OauthClientDetails application={application} oauthClient={oauthClient} navigateTo={navigateTo} />;
  }

  if (resourcePage === SettingsResourcePagesEnum.NEW || resourcePage === SettingsResourcePagesEnum.EDIT) {
    const onSaveOauthClient = async (unsavedOauthClient: UnsavedOauthClientModel) => {
      if (!application) {
        return;
      }

      await saveOauthClientAsync(application, unsavedOauthClient);

      if (resourcePage === SettingsResourcePagesEnum.NEW) {
        onRefetchApplication();
      } else {
        await refetch();
      }
    };

    return (
      <OauthClientEditorWrapper
        panelId={panelId}
        application={application}
        oauthClient={oauthClient}
        onSaveOauthClient={onSaveOauthClient}
        navigateTo={navigateTo}
      />
    );
  }

  return null;
};

/*
 * Helpers.
 */

function computePageTitle(url: SettingsOauthClientUrl, application?: LocalApplicationModel) {
  if (url.resourcePage === SettingsResourcePagesEnum.NEW) {
    return <Trans id="oauth-client-new-title">Create OAuth client</Trans>;
  }
  if (url.resourcePage === SettingsResourcePagesEnum.EDIT) {
    return <Trans id="oauth-client-edit-title">Edit OAuth client for {application?.name}</Trans>;
  }
  return application?.name;
}
